import React, { useCallback, useEffect, useMemo, useState } from "react";
import { TableCompareTrigger } from "../../../components/lcaComparison";
import { useTranslation } from "react-i18next";
import { IconButton } from "@mui/material";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import { unitFromDropdownOrCache } from "../../../../../CustomHeadlessTable/lcaDropdownUnit";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";
import {
  addFlow42_1LCA,
  deleteFlow42_1LCA,
  getAllFlow42_1LCA,
  updateFlow42_1LCA,
} from "../../../../../../apis/Product/EndOfLifeFlow42Api";
import {
  getProductFlow42_1MaterialEnergyDropdown,
  getProductDisposalMethodMaintenance41_2bDropdown,
} from "../../../../../../apis/Dropdown";

// Impact categories configuration (16)
const IMPACT_CATEGORIES = [
  { name: "Climate change", unit: "kg CO₂-eq", key: "global_warming" },
  { name: "Ozone depletion", unit: "kg CFC-11-eq", key: "ozone_depletion" },
  { name: "Photochemical ozone formation", unit: "kg NMVOC-eq", key: "smog" },
  { name: "Acidification", unit: "mol H⁺-eq", key: "acidification" },
  {
    name: "Eutrophication, terrestrial",
    unit: "mol N-eq",
    key: "eutrophication_terrestrial",
  },
  {
    name: "Eutrophication, freshwater",
    unit: "kg P-eq",
    key: "eutrophication_freshwater",
  },
  { name: "Eutrophication, marine", unit: "kg N-eq", key: "eutrophication_marine" },
  { name: "Human toxicity, cancer effects", unit: "CTUh", key: "carcinogenics" },
  {
    name: "Human toxicity, non-cancer effects",
    unit: "CTUh",
    key: "non_carcinogenics",
  },
  { name: "Particulate matter", unit: "disease incidence", key: "respiratory_effects" },
  { name: "Ecotoxicity, freshwater", unit: "CTUe", key: "ecotoxicity" },
  { name: "Resource use, energy carriers", unit: "MJ", key: "fossil_fuel_depletion" },
  { name: "Land use", unit: "Pt", key: "land_use" },
  { name: "Water use", unit: "m³ world-eq deprived", key: "water_use" },
  {
    name: "Resource use, mineral and metals",
    unit: "kg Sb-eq",
    key: "resource_use_minerals_metals",
  },
  {
    name: "Ionising radiation, human health",
    unit: "kBq U235-eq",
    key: "ionising_radiation",
  },
];

const Flow42_1Table = ({ productId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [currentImpactCategoryIndex, setCurrentImpactCategoryIndex] = useState(0);
  const [materialEnergyDropdown, setMaterialEnergyDropdown] = useState([]);
  const [disposalMethodDropdown, setDisposalMethodDropdown] = useState([]);
  const [unitMaterials, setUnitMaterials] = useState({});
  const [unitDisposal, setUnitDisposal] = useState({});
  const [materialTypes, setMaterialTypes] = useState({}); // Track if material is fuel/electricity

  // Fetch dropdowns
  useEffect(() => {
    getProductFlow42_1MaterialEnergyDropdown({
      setDropdown: setMaterialEnergyDropdown,
    });
    getProductDisposalMethodMaintenance41_2bDropdown({
      setDropdown: setDisposalMethodDropdown,
    });
  }, []);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    if (!productId) return;
    getAllFlow42_1LCA({
      productId,
      setObjects: setData,
    });
  }, [productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Update units and material types when data or dropdown changes
  useEffect(() => {
    if (data.length > 0 && materialEnergyDropdown.length > 0) {
      const newUnits = {};
      const newMaterialTypes = {};
      data.forEach((row) => {
        const materialCode =
          typeof row.what_is_added_material_energy === "string"
            ? row.what_is_added_material_energy
            : row.what_is_added_material_energy?.value || row.what_is_added_material_energy?.code;
        if (materialCode) {
          const selected = materialEnergyDropdown.find(
            (opt) =>
              !opt.isCategory &&
              (opt.code === materialCode || opt.value === materialCode)
          );
          if (selected) {
            newUnits[row.id] = selected.unit || "xx";
            // Check if material is fuel or electricity (you may need to adjust this logic based on your data)
            const materialName = (selected.name || "").toLowerCase();
            const isFuelOrElectricity = 
              materialName.includes("fuel") || 
              materialName.includes("electricity") || 
              materialName.includes("electric");
            newMaterialTypes[row.id] = isFuelOrElectricity;
            
            // If fuel/electricity and disposal qty is null, set it to "-" for display
            if (isFuelOrElectricity && (row.quantity_per_fu_disposal === null || row.quantity_per_fu_disposal === undefined)) {
              setData((prev) => {
                const updated = prev.map((item) =>
                  item.id === row.id
                    ? { ...item, quantity_per_fu_disposal: "-" }
                    : item
                );
                return updated;
              });
            }
          }
        }
      });
      setUnitMaterials(newUnits);
      setMaterialTypes(newMaterialTypes);
    }
  }, [data, materialEnergyDropdown]);

  // Update disposal units
  useEffect(() => {
    if (data.length > 0 && disposalMethodDropdown.length > 0) {
      const newUnits = {};
      data.forEach((row) => {
        const disposalCode =
          typeof row.disposal_method === "string"
            ? row.disposal_method
            : row.disposal_method?.value || row.disposal_method?.code;
        if (disposalCode) {
          const selected = disposalMethodDropdown.find(
            (opt) =>
              !opt.isCategory &&
              (opt.code === disposalCode || opt.value === disposalCode)
          );
          if (selected) {
            newUnits[row.id] = selected.unit || "xx";
          }
        }
      });
      setUnitDisposal(newUnits);
    }
  }, [data, disposalMethodDropdown]);

  const dynamicPlaceholders = useMemo(
    () => ({
      what_is_added_material_energy: {
        targetField: "quantity_per_fu_material_energy",
        getPlaceholder: (selectedValue, dropdownData, _rowData, rowId = "new") => {
          const selected = dropdownData?.find(
            (opt) => opt?.value === selectedValue || opt?.code === selectedValue
          );
          if (selected) {
            setUnitMaterials((prev) => ({
              ...prev,
              [rowId]: selected.unit || "xx",
            }));
            // Check if material is fuel/electricity
            const materialName = (selected.name || "").toLowerCase();
            const isFuelOrElectricity = 
              materialName.includes("fuel") || 
              materialName.includes("electricity") || 
              materialName.includes("electric");
            setMaterialTypes((prev) => ({
              ...prev,
              [rowId]: isFuelOrElectricity,
            }));
            // If fuel/electricity, auto-fill disposal qty with "-"
            if (isFuelOrElectricity) {
              setData((prev) => {
                const updated = prev.map((item) =>
                  item.id === rowId
                    ? { ...item, quantity_per_fu_disposal: "-" }
                    : item
                );
                return updated;
              });
            }
          }
        },
        dropdownDataKey: "what_is_added_material_energy",
      },
      disposal_method: {
        targetField: "quantity_per_fu_disposal",
        getPlaceholder: (selectedValue, dropdownData, _rowData, rowId = "new") => {
          const selected = dropdownData?.find(
            (opt) => opt?.value === selectedValue || opt?.code === selectedValue
          );
          if (selected) {
            setUnitDisposal((prev) => ({
              ...prev,
              [rowId]: selected.unit || "xx",
            }));
          }
        },
        dropdownDataKey: "disposal_method",
      },
    }),
    []
  );

  const handleEdit = useCallback(
    (editedItem) => {
      const materialCode =
        typeof editedItem.what_is_added_material_energy === "string"
          ? editedItem.what_is_added_material_energy
          : editedItem.what_is_added_material_energy?.value || 
            editedItem.what_is_added_material_energy?.code || 
            null;

      const disposalCode =
        typeof editedItem.disposal_method === "string"
          ? editedItem.disposal_method
          : editedItem.disposal_method?.value || 
            editedItem.disposal_method?.code || 
            null;

      // If material is fuel/electricity, set disposal qty to "-"
      const isFuelOrElectricity = materialTypes[editedItem.id];
      const disposalQty = isFuelOrElectricity 
        ? "-" 
        : (editedItem.quantity_per_fu_disposal || 0);

      updateFlow42_1LCA({
        productId,
        rowId: editedItem.id,
        name_deconstruction_sorting_processes: editedItem.name_deconstruction_sorting_processes || null,
        describe_the_process: editedItem.describe_the_process || null,
        what_is_added_material_energy: materialCode,
        quantity_per_fu_material_energy: editedItem.quantity_per_fu_material_energy || 0,
        disposal_method: disposalCode,
        quantity_per_fu_disposal: disposalQty === "-" ? null : disposalQty,
        objects: data,
        setObjects: setData,
        use_swal: true,
        callback: () => {
          window.dispatchEvent(new Event('refresh-end-of-life-42-totals'));
        },
      });
    },
    [productId, data, materialTypes]
  );

  const handleAdd = useCallback(
    (newItem) => {
      const materialCode =
        typeof newItem.what_is_added_material_energy === "string"
          ? newItem.what_is_added_material_energy
          : newItem.what_is_added_material_energy?.value || 
            newItem.what_is_added_material_energy?.code || 
            null;

      const disposalCode =
        typeof newItem.disposal_method === "string"
          ? newItem.disposal_method
          : newItem.disposal_method?.value || 
            newItem.disposal_method?.code || 
            null;

      // Check if material is fuel/electricity
      const selectedMaterial = materialEnergyDropdown.find(
        (opt) => !opt.isCategory && (opt.code === materialCode || opt.value === materialCode)
      );
      const isFuelOrElectricity = selectedMaterial && (
        (selectedMaterial.name || "").toLowerCase().includes("fuel") ||
        (selectedMaterial.name || "").toLowerCase().includes("electricity") ||
        (selectedMaterial.name || "").toLowerCase().includes("electric")
      );

      const disposalQty = isFuelOrElectricity 
        ? null 
        : (newItem.quantity_per_fu_disposal || 0);

      addFlow42_1LCA({
        productId,
        name_deconstruction_sorting_processes: newItem.name_deconstruction_sorting_processes || null,
        describe_the_process: newItem.describe_the_process || null,
        what_is_added_material_energy: materialCode,
        quantity_per_fu_material_energy: newItem.quantity_per_fu_material_energy || 0,
        disposal_method: isFuelOrElectricity ? null : disposalCode,
        quantity_per_fu_disposal: disposalQty,
        callback: () => {
          fetchData();
          window.dispatchEvent(new Event('refresh-end-of-life-42-totals'));
        },
        use_swal: true,
      });
    },
    [productId, fetchData, materialEnergyDropdown]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteFlow42_1LCA({
        productId,
        rowId: itemToDelete.id,
        callback: () => {
          fetchData();
          window.dispatchEvent(new Event('refresh-end-of-life-42-totals'));
        },
      });
    },
    [productId, fetchData]
  );

  const currentImpactCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
  const impactCategoryAccessor = currentImpactCategory.key;

  const handlePreviousImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev === 0 ? IMPACT_CATEGORIES.length - 1 : prev - 1
    );
  }, []);

  const handleNextImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev === IMPACT_CATEGORIES.length - 1 ? 0 : prev + 1
    );
  }, []);

  // Check if disposal method column should be disabled for a row
  const isDisposalDisabled = useCallback((rowData) => {
    const materialCode =
      typeof rowData.what_is_added_material_energy === "string"
        ? rowData.what_is_added_material_energy
        : rowData.what_is_added_material_energy?.value || 
          rowData.what_is_added_material_energy?.code;
    
    if (!materialCode) return true;
    
    const isFuelOrElectricity = materialTypes[rowData.id];
    return isFuelOrElectricity;
  }, [materialTypes]);

  const columns = useMemo(
    () => [
      {
        id: "name_deconstruction_sorting_processes",
        accessor: "name_deconstruction_sorting_processes",
        header: t("NAME_DECONSTRUCTION_SORTING_PROCESSES") || "Name deconstruction and sorting processes",
        type: "text",
        placeholder: t("USER_TYPES") || "User Types...",
        optional: true,
      },
      {
        id: "describe_the_process",
        accessor: "describe_the_process",
        header: t("DESCRIBE_THE_PROCESS") || "Describe the process",
        type: "text",
        placeholder: t("USER_TYPES") || "User Types...",
        optional: true,
      },
      {
        id: "what_is_added_material_energy",
        accessor: "what_is_added_material_energy",
        header: `${t("WHAT_IS_ADDED_MATERIAL_ENERGY") || "What is added material / energy"} *`,
        type: "dropdown",
        isCategory: true,
        placeholder: t("SELECT_MATERIAL_ENERGY") || "Select material/energy...",
        unit: (rowId, rowData) =>
          unitFromDropdownOrCache(
            rowData,
            "what_is_added_material_energy",
            materialEnergyDropdown,
            unitMaterials,
            rowId
          ),
      },
      {
        id: "quantity_per_fu_material_energy",
        accessor: "quantity_per_fu_material_energy",
        header: `${t("QUANTITY_PER_FU") || "Quantity per functional unit"} *`,
        type: "number",
        placeholder: t("USER_TYPES") || "User Types...",
        unit: (rowId, rowData) =>
          unitFromDropdownOrCache(
            rowData,
            "what_is_added_material_energy",
            materialEnergyDropdown,
            unitMaterials,
            rowId
          ),
      },
      {
        id: "disposal_method",
        accessor: "disposal_method",
        header: `${t("DISPOSAL_METHOD_LEFT_AFTER_PROCESS") || "Disposal method of what is left after the process (e.g. throwing out old screws etc)"} *`,
        type: "dropdown",
        isCategory: true,
        placeholder: t("SELECT_DISPOSAL_METHOD") || "Select disposal method...",
        optional: true,
        unit: (rowId, rowData) =>
          unitFromDropdownOrCache(
            rowData,
            "disposal_method",
            disposalMethodDropdown,
            unitDisposal,
            rowId
          ),
      },
      {
        id: "quantity_per_fu_disposal",
        accessor: "quantity_per_fu_disposal",
        header: `${t("QUANTITY_PER_FU") || "Quantity per functional unit"} *`,
        type: "number",
        placeholder: t("USER_TYPES") || "User Types...",
        unit: (rowId, rowData) =>
          unitFromDropdownOrCache(
            rowData,
            "disposal_method",
            disposalMethodDropdown,
            unitDisposal,
            rowId
          ),
        optional: true,
      },
      {
        id: currentImpactCategory.key,
        header: (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <IconButton
              size="small"
              onClick={handlePreviousImpactCategory}
              style={{ padding: "4px" }}
            >
              <KeyboardArrowLeft fontSize="small" />
            </IconButton>
            <span>
              {currentImpactCategory.name} [{currentImpactCategory.unit}]
            </span>
            <IconButton
              size="small"
              onClick={handleNextImpactCategory}
              style={{ padding: "4px" }}
            >
              <KeyboardArrowRight fontSize="small" />
            </IconButton>
          <TableCompareTrigger />
          </div>
        ),
        accessor: currentImpactCategory.key,
        type: "custom",
        optional: true,
        canSort: false,
        cell: ({ row }) => {
          const currentCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
          const impactValue =
            row.original[currentCategory.key] ||
            row.original[`impact_${currentCategory.key}`] ||
            row.original.impact_categories?.[currentCategory.key] ||
            0;
          return <CustomInputComponent value={impactValue} readOnly />;
        },
        subtotalUnit: currentImpactCategory.unit,
        subtotalFixed: true,
        width: "200px",
      },
    ],
    [
      t,
      unitMaterials,
      unitDisposal,
      materialEnergyDropdown,
      disposalMethodDropdown,
      data,
      currentImpactCategory,
      currentImpactCategoryIndex,
      handlePreviousImpactCategory,
      handleNextImpactCategory,
    ]
  );

  const disableRules = useMemo(
    () => ({
      [impactCategoryAccessor]: () => true,
      disposal_method: isDisposalDisabled,
      quantity_per_fu_disposal: (rowData) => {
        const isDisabled = isDisposalDisabled(rowData);
        if (isDisabled) {
          // Auto-fill with "-" if disabled
          return true;
        }
        return false;
      },
    }),
    [impactCategoryAccessor, isDisposalDisabled]
  );

  const subtotalColumns = useMemo(
    () => ({
      [impactCategoryAccessor]: { label: `Total ${currentImpactCategory.name}` },
    }),
    [impactCategoryAccessor, currentImpactCategory.name]
  );

  const memoizedDropdownData = useMemo(
    () => ({
      what_is_added_material_energy: materialEnergyDropdown || [],
      disposal_method: disposalMethodDropdown || [],
    }),
    [materialEnergyDropdown, disposalMethodDropdown]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={useMemo(
        () => ({
          [impactCategoryAccessor]: CustomInputComponent,
        }),
        [impactCategoryAccessor]
      )}
      comparisonConfig={{ categories: IMPACT_CATEGORIES }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
      dynamicPlaceholders={dynamicPlaceholders}
    />
  );
};

export default Flow42_1Table;

